/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent'
import { Form, FormikProvider, useFormik } from 'formik'
import Box from '@mui/material/Box'
import { ArrowDownward, ArrowRightAlt, KeyboardArrowRight } from '@mui/icons-material'

import { pythonHttpClient } from 'shared/lib'
import InputField from 'shared/components/InputField'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'

import CustomDialog from 'shared/components/CustomDialog'
import { DialogActions, Divider } from '@mui/material'
import { EditDescType } from '../../types'
import { editDescSchema } from './schema'

interface Props {
   open: boolean
   handleClose: () => void
   productUuid: string
}

export default function EditDescriptionModal({ open, handleClose, productUuid }: Props) {
   const { addNotification } = useNotificationContext()
   const [isLoading, setIsLoading] = useState(false)

   const formik = useFormik<EditDescType>({
      initialValues: {
         countries: [],
         translations: [],
      },
      validationSchema: editDescSchema,
      onSubmit: handleSave,
   })

   const { values, setValues, setFieldValue } = formik

   function handleSave() {
      setIsLoading(true)
      const payload = [
         ...values.translations.map((item) => ({
            name: item.name,
            description: item.description,
            countryCode: item.countryCode,
         })),
      ]
      pythonHttpClient
         .patch(`products/${productUuid}/description`, payload)
         .then((res) => {
            if (res.status === 204) {
               addNotification('successSave', 'success')
               handleClose()
            }
         })
         .catch(() => addNotification('failedSave', 'error'))
         .finally(() => setIsLoading(false))
   }

   useEffect(() => {
      setIsLoading(true)
      pythonHttpClient
         .get(`/products/${productUuid}/description`)
         .then((res) => {
            setValues({
               ...res.data,
            })
         })
         .finally(() => setIsLoading(false))
   }, [])

   return (
      <CustomDialog
         maxWidth="xl"
         isLoading={!open}
         open={open}
         onClose={handleClose}
         content={
            <>
               <DialogTitle>
                  <Typography color="primary" variant="h4">
                     Edytuj opisy hurtowni
                  </Typography>
               </DialogTitle>
               <DialogContent>
                  <FormikProvider value={formik}>
                     <Form>
                        <Box
                           sx={{
                              py: { lg: 2, md: 2, xs: 0 },
                              px: { lg: 4, md: 4, xs: 0 },
                              minWidth: { lg: 800, md: 800, xs: 'auto' },
                              display: 'flex',
                              maxHeight: 700,
                              overflow: 'auto',
                              flexDirection: 'column',
                              gap: 2,
                           }}
                        >
                           {values.translations.map((transl, index) => (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                 <Divider flexItem>{transl.countryCode}</Divider>
                                 <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                                    <InputField
                                       disabled={isLoading}
                                       fullWidth
                                       sx={{ minWidth: 100 }}
                                       name={`translations[${index}].name`}
                                       label="Nazwa produktu"
                                       multiline
                                       rows={4}
                                    />
                                    <ContainedButton
                                       sx={{ alignSelf: 'center' }}
                                       variant="contained"
                                       onClick={() => {
                                          setFieldValue(
                                             `translations[${index}].description`,
                                             values.translations[index].name
                                          )
                                       }}
                                    >
                                       <ArrowRightAlt />
                                    </ContainedButton>
                                    <InputField
                                       disabled={isLoading}
                                       fullWidth
                                       sx={{ minWidth: 100 }}
                                       name={`translations[${index}].description`}
                                       label="Opis produktu"
                                       multiline
                                       rows={4}
                                    />
                                 </Box>
                              </Box>
                           ))}
                        </Box>
                     </Form>
                  </FormikProvider>
               </DialogContent>
               <DialogActions>
                  <OutlinedButton variant="outlined" onClick={handleClose}>
                     Anuluj
                  </OutlinedButton>
                  <ContainedButton disabled={isLoading} onClick={handleSave} variant="contained" type="submit">
                     Zapisz
                  </ContainedButton>
               </DialogActions>
            </>
         }
      />
   )
}
